import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, ArrowRight, Sparkles } from 'lucide-react';
import TextOnlyPostArt from '@/components/social/TextOnlyPostArt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/** Homepage "The Feed" preview band — pulls 6 recent posts from /social/feed
 *  in a compact horizontal-scrollable card row. Hides itself entirely if
 *  there are no posts yet (clean homepage during cold start). */
export default function HomeFeedPreview() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/social/feed?tab=foryou&limit=6`);
        if (res.ok) setPosts((await res.json()).posts || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  // Show "The Feed" preview as soon as there is at least 1 post — keeps the
  // homepage alive during early community growth.
  if (posts.length === 0) return null;

  return (
    <section
      className="relative py-16 sm:py-20 overflow-hidden bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B]"
      data-testid="home-feed-preview"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-10 w-96 h-96 rounded-full bg-fuchsia-600/15 blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-cyan-500/15 blur-3xl"></div>
      </div>

      <div className="container-main relative">
        <div className="flex items-end justify-between mb-7 sm:mb-10 gap-3 flex-wrap">
          <div>
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm text-fuchsia-300 font-semibold tracking-widest uppercase mb-2">
              <Sparkles size={14} /> The Feed
            </p>
            <h2 className="font-syne text-2xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
              Indigenous entrepreneurs,{' '}
              <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
                their stories
              </span>
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-xl">
              Live from the community — vendor posts, behind-the-scenes, and culture in motion.
            </p>
          </div>
          <Link
            to="/feed"
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold shadow-[0_8px_24px_-8px_rgba(250,204,21,0.6)]"
            data-testid="home-feed-cta"
          >
            Open the Feed <ArrowRight size={14} />
          </Link>
        </div>

        {/* Horizontal scroll on mobile, grid on desktop */}
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:mx-0 md:px-0">
          {posts.map((p) => {
            const first = (p.media && p.media[0]) || (p.media_url ? { url: p.media_url, type: p.media_type } : null);
            return (
            <Link
              key={p.post_id}
              to="/feed"
              className="snap-start flex-shrink-0 w-[78%] xs:w-[60%] md:w-auto group rounded-2xl bg-[#13153A]/80 border border-white/5 hover:border-fuchsia-500/30 backdrop-blur-md overflow-hidden transition-all hover:shadow-[0_12px_40px_-12px_rgba(217,70,239,0.5)]"
              data-testid={`home-feed-post-${p.post_id}`}
            >
              <div className="aspect-square bg-black w-full overflow-hidden relative">
                {first ? (first.type === 'video' ? (
                  <video
                    src={first.url}
                    className="w-full h-full object-cover"
                    muted
                    loop
                    playsInline
                    autoPlay
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={first.url}
                    alt={p.caption || 'Post'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs px-4 text-center">
                    {p.caption?.slice(0, 120) || ''}
                  </div>
                )}
                {p.media && p.media.length > 1 && (
                  <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
                    +{p.media.length - 1}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-3 flex items-center justify-between text-xs text-white">
                  <div className="flex items-center gap-2 min-w-0">
                    {p.author?.profile_image ? (
                      <img src={p.author.profile_image} alt="" className="w-6 h-6 rounded-full object-cover border border-white/30" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-fuchsia-500/40 flex items-center justify-center text-[10px] font-bold">
                        {(p.author?.name || '?')[0]}
                      </div>
                    )}
                    <span className="truncate font-semibold">{p.author?.name}</span>
                  </div>
                  <span className="text-zinc-300 text-[10px]">{fmtAgo(p.created_at)}</span>
                </div>
              </div>
              <div className="p-3">
                {p.caption && (
                  <p className="text-zinc-200 text-xs line-clamp-2 mb-2">{p.caption}</p>
                )}
                <div className="flex items-center gap-3 text-zinc-400 text-[11px]">
                  <span className="flex items-center gap-1"><Heart size={11} /> {p.likes_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={11} /> {p.comments_count || 0}</span>
                  {p.can_book && (
                    <span className="ml-auto text-yellow-400 font-bold uppercase tracking-wider text-[10px]">Book Now →</span>
                  )}
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
